import * as React from "react";
import {useMemo, useState} from "react";
import {
    Badge,
    classNames,
    CloseButton,
    layout,
    theme,
    typography,
    useOccupancies,
    View,
    ViewContent,
    ViewHeader,
    ViewHeaderItem
} from "@variocube/cube-ui";
import {Item, RentalTypes} from "./types";
import {BoxControl} from "./admin/BoxControl";
import ImageUndrawDelivery from "./undraw_logistics_x-4-dc.svg";
import {stylesheet} from "typestyle";
import {useLocalization} from "../i18n";
import {useTenant} from "./hooks";


export interface ItemAccessViewProps {
    item: Item;
    rentalTypes: RentalTypes;
    onClose: () => void;
}

export function ItemAccessView({item, rentalTypes, onClose}: ItemAccessViewProps) {
    const {t, language} = useLocalization();
    const occupancies = useOccupancies();
    const tenant = useTenant();
    const occupancy = occupancies.find(occupancy => occupancy.content.item == item.uuid);
    const [boxOpened, setBoxOpened] = useState<boolean>(false);
    const infoText = useMemo(() => (tenant && tenant.rentalInfo) ? tenant.rentalInfo[language as "en"|"de"] : undefined, [tenant, language]);

    return (
        <View>
            <ViewHeader className={theme.lessContrast}>
                <ViewHeaderItem fill className={layout.paddingMedium}>
                    <div className={typography.title}>{rentalTypes == RentalTypes.Loan ? t("Loan") : t("Rental")}</div>
                </ViewHeaderItem>
                <CloseButton label={t("Close")} onClick={onClose}/>
            </ViewHeader>
            <ViewContent className={classNames(layout.horizontal, layout.paddingMedium, layout.alignCenter, layout.spaceEvenly)}>
                <div className={typography.bigger}>
                    <div className={layout.marginYLarge}>
                        {!boxOpened &&
                            <React.Fragment>
                                <div className={typography.caption}>{t("Item")}</div>
                                <div className={layout.marginYSmall}>
                                    <div>{item?.name}</div>
                                    <div>{item?.description}</div>
                                    <div>{item?.foreignId}</div>
                                </div>
                                {infoText &&
                                    <div className={layout.marginYLarge}>
                                        <div style={{whiteSpace: 'pre-wrap'}}>{infoText}</div>
                                    </div>
                                }
                            </React.Fragment>
                        }
                        {boxOpened &&
                            <div className={layout.marginYLarge}>
                                <div className={styles.deliveryImage}/>
                                <div className={layout.marginYSmall}>
                                    <div>{t("PickupItem")}</div>
                                </div>
                            </div>
                        }
                    </div>
                </div>

                <div className={classNames(layout.fill, layout.vertical, layout.center, layout.alignCenter)}>
                    {!occupancy && (
                        <div className={typography.bigger} style={{maxWidth: "20em", paddingBottom: "1em"}}>
                            <Badge error>{t("ItemNotStoredError")}</Badge>
                        </div>
                    )}
                    {tenant && !occupancy &&
                    <div
                        className={classNames(layout.fill, layout.horizontal, layout.alignStart)}
                        style={{width: "20em", paddingBottom: "1em"}}
                    >
                        <div className={classNames(layout.fill, layout.vertical, layout.alignStart, typography.bigger)}>
                            <div><strong>{tenant.name}</strong></div>
                            {tenant.email && <div>{tenant.email}</div>}
                            {tenant.phone && <div>{tenant.phone}</div>}
                            {tenant.web && <div>{tenant.web}</div>}
                        </div>
                    </div>
                    }
                    {occupancy && (
                        <div
                            className={classNames(layout.marginMedium)}>
                            <div className={typography.title}>{t("Box")}</div>
                            {occupancy && (
                                <BoxControl boxNumber={occupancy.boxNumber} onBoxOpened={() => setBoxOpened(true)} />
                            )}
                        </div>
                    )}
                </div>
            </ViewContent>
        </View>
    );
}

export const styles = stylesheet({
    deliveryImage: {
        backgroundPosition: "center center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        height: "7em",
        width: "14em",
        margin: "1em auto",
        backgroundImage: `url(${ImageUndrawDelivery})`,
    }
});